<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { ExceptionWithMessage } from 'shared/helpers/CustomErrors';
import {
  searchContacts,
  createNewContact,
  fetchContactableInboxes,
  mergeInboxDetails,
} from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper';
import ComposeNewConversationForm from 'dashboard/components-next/NewConversation/components/ComposeNewConversationForm.vue';
import MessageList from 'dashboard/components-next/message/MessageList.vue';
import ReplyBox from 'dashboard/components/widgets/conversation/ReplyBox.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import ConversationApi from 'dashboard/api/inbox/conversation';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

const props = defineProps({
  ticket: {
    type: Object,
    required: true,
  },
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'emailSent']);

const store = useStore();
const { t } = useI18n();
const { fetchSignatureFlagFromUISettings } = useUISettings();

const contacts = ref([]);
const selectedContact = ref(null);
const targetInbox = ref(null);
const isCreatingContact = ref(false);
const isFetchingInboxes = ref(false);
const isSearching = ref(false);
const isSending = ref(false);
const conversation = ref(null);
const isLoadingConversation = ref(false);

const contactById = useMapGetter('contacts/getContactById');
const contactsUiFlags = useMapGetter('contacts/getUIFlags');
const currentUser = useMapGetter('getCurrentUser');
const globalConfig = useMapGetter('globalConfig/get');
const uiFlags = useMapGetter('contactConversations/getUIFlags');
const messageSignature = useMapGetter('getMessageSignature');
const inboxesList = useMapGetter('inboxes/getInboxes');
const currentAccountId = useMapGetter('getCurrentAccountId');
const getConversationById = useMapGetter('getConversationById');
const selectedChat = useMapGetter('getSelectedChat');

const sendWithSignature = computed(() =>
  fetchSignatureFlagFromUISettings(targetInbox.value?.channelType)
);

const directUploadsEnabled = computed(
  () => globalConfig.value.directUploadsEnabled
);

const hasExistingConversation = computed(() => !!props.ticket?.conversation_id);

// Check if conversation is email channel
const isEmailConversation = computed(() => {
  if (!conversation.value) return false;
  return conversation.value.meta?.channel === INBOX_TYPES.EMAIL;
});

// Fetch conversation data
const fetchConversation = async () => {
  if (!props.ticket?.conversation_id) {
    conversation.value = null;
    return;
  }
  // Conversations API expects display_id in the URL, not the internal conversation id
  const displayId = props.ticket.conversation_display_id;
  if (!displayId) {
    conversation.value = null;
    return;
  }

  isLoadingConversation.value = true;
  try {
    // First check if conversation is already in store (store keys by display_id)
    let fetchedConversation = null;
    if (getConversationById.value && typeof getConversationById.value === 'function') {
      fetchedConversation = getConversationById.value(displayId);
    }
    
    // If not in store, fetch it using API directly
    if (!fetchedConversation) {
      const response = await ConversationApi.show(displayId);
      fetchedConversation = response.data;
      
      // Add to store using the action which handles mutations correctly
      if (fetchedConversation) {
        try {
          await store.dispatch('updateConversation', fetchedConversation);
        } catch (error) {
          console.error('Error updating conversation in store:', error);
        }
      }
    }
    
    // Only set conversation if it's an email channel
    if (fetchedConversation?.meta?.channel === INBOX_TYPES.EMAIL) {
      // Ensure conversation has messages array
      if (!fetchedConversation.messages) {
        fetchedConversation.messages = [];
      }
      
      conversation.value = fetchedConversation;
      
      // Set as current chat so ReplyBox works correctly
      // This must be done before rendering ReplyBox
      try {
        await store.dispatch('setActiveChat', {
          data: fetchedConversation,
        });
        // Wait for Vue reactivity to update the getter
        await new Promise(resolve => {
          setTimeout(resolve, 0);
        });
      } catch (error) {
        console.error('Error setting active chat:', error);
        conversation.value = null;
      }
    } else {
      conversation.value = null;
    }
  } catch (error) {
    console.error('Error fetching conversation:', error);
    conversation.value = null;
  } finally {
    isLoadingConversation.value = false;
  }
};

// Initialize contact from ticket
const initializeContact = async () => {
  if (!props.ticket?.contact_id) return;

  const contact = contactById.value && typeof contactById.value === 'function'
    ? contactById.value(props.ticket.contact_id)
    : null;
  if (contact) {
    selectedContact.value = contact;
    
    // If there's a conversation, we don't need to fetch inboxes
    if (hasExistingConversation.value) {
      await fetchConversation();
      return;
    }
    
    // Fetch contactable inboxes only if no conversation exists
    if (contact.id) {
      isFetchingInboxes.value = true;
      try {
        const contactableInboxes = await fetchContactableInboxes(contact.id);
        selectedContact.value.contactInboxes = mergeInboxDetails(
          contactableInboxes,
          inboxesList.value
        );
        
        // Auto-select email inbox if available
        const emailInbox = contactableInboxes.find(
          inbox => inbox.channelType === 'email'
        );
        if (emailInbox) {
          targetInbox.value = mergeInboxDetails([emailInbox], inboxesList.value)[0];
        }
      } catch (error) {
        // Error fetching inboxes - silently fail
      } finally {
        isFetchingInboxes.value = false;
      }
    }
  } else {
    // Fetch contact if not in store
    try {
      await store.dispatch('contacts/get', props.ticket.contact_id);
      const fetchedContact = contactById.value && typeof contactById.value === 'function'
        ? contactById.value(props.ticket.contact_id)
        : null;
      if (fetchedContact) {
        selectedContact.value = fetchedContact;
        if (hasExistingConversation.value) {
          await fetchConversation();
        }
      }
    } catch (error) {
      // Error fetching contact
    }
  }
};

const onContactSearch = async query => {
  isSearching.value = true;
  contacts.value = [];
  try {
    contacts.value = await searchContacts(query);
  } catch (error) {
    useAlert(t('COMPOSE_NEW_CONVERSATION.CONTACT_SEARCH.ERROR_MESSAGE'));
  } finally {
    isSearching.value = false;
  }
};

const resetContacts = () => {
  contacts.value = [];
};

const handleSelectedContact = async ({ value, action, ...rest }) => {
  let contact;
  if (action === 'create') {
    isCreatingContact.value = true;
    try {
      contact = await createNewContact(value);
      isCreatingContact.value = false;
    } catch (error) {
      isCreatingContact.value = false;
      return;
    }
  } else {
    contact = rest;
  }
  selectedContact.value = contact;
  if (contact?.id) {
    isFetchingInboxes.value = true;
    try {
      const contactableInboxes = await fetchContactableInboxes(contact.id);
      selectedContact.value.contactInboxes = mergeInboxDetails(
        contactableInboxes,
        inboxesList.value
      );
      isFetchingInboxes.value = false;
    } catch (error) {
      isFetchingInboxes.value = false;
    }
  }
};

const handleTargetInbox = inbox => {
  targetInbox.value = inbox;
  resetContacts();
};

const clearSelectedContact = () => {
  selectedContact.value = null;
  targetInbox.value = null;
};

const createConversation = async ({ payload, isFromWhatsApp }) => {
  try {
    isSending.value = true;
    
    // Create a new conversation
    const data = await store.dispatch('contactConversations/create', {
      params: payload,
      isFromWhatsApp,
    });

    // Associate conversation with ticket
    if (data?.id && props.ticket?.id) {
      await store.dispatch('tickets/update', {
        id: props.ticket.id,
        data: { conversation_id: data.id },
      });
      
      // Fetch the conversation to show messages
      await fetchConversation();
    }

    useAlert(t('TICKETS.EMAIL_SENT_SUCCESS'));
    emit('emailSent');
    // Don't close immediately - let user see the conversation
    return true;
  } catch (error) {
    if (
      error instanceof ExceptionWithMessage &&
      error.data?.type === 'contact_has_active_conversations'
    ) {
      const conversations = error.data.conversations || [];
      if (conversations.length > 0) {
        const links = conversations.map(c => ({
          label: `#${c[1]}`,
          to: `/app/accounts/${currentAccountId.value}/conversations/${c[1]}`,
        }));
        const action = {
          type: 'multi-link',
          links,
        };
        useAlert(
          t('COMPOSE_NEW_CONVERSATION.FORM.ERROR_MESSAGE_HAS_CONVERSATION'),
          action
        );
      } else {
        useAlert(
          t('COMPOSE_NEW_CONVERSATION.FORM.ERROR_MESSAGE_HAS_CONVERSATION')
        );
      }
    } else {
      useAlert(
        error instanceof ExceptionWithMessage
          ? error.data
          : t('TICKETS.EMAIL_SENT_ERROR')
      );
    }
    return false;
  } finally {
    isSending.value = false;
  }
};


const closeCompose = () => {
  // Clear current chat when closing
  if (conversation.value) {
    store.dispatch('clearSelectedState');
  }
  emit('close');
};

watch(
  () => props.show,
  async newValue => {
    if (newValue) {
      await initializeContact();
    } else {
      // Reset form when closing
      selectedContact.value = null;
      targetInbox.value = null;
      conversation.value = null;
      resetContacts();
      if (conversation.value) {
        store.dispatch('clearSelectedState');
      }
    }
  },
  { immediate: true }
);

watch(
  () => props.ticket?.conversation_id,
  async (newConversationId) => {
    if (newConversationId && props.show) {
      await fetchConversation();
    }
  }
);


onMounted(() => {
  if (props.show) {
    initializeContact();
  }
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="show"
      class="fixed z-[10000] bg-n-alpha-black1 backdrop-blur-[4px] flex items-center justify-center inset-0"
      @click.self="closeCompose"
    >
    <div
      class="bg-n-background rounded-lg shadow-xl w-[90vw] h-[90vh] max-w-6xl flex flex-col overflow-hidden"
    >
      <!-- Header -->
      <div class="flex items-center justify-between p-4 border-b border-n-weak">
        <h2 class="text-lg font-semibold text-n-slate-12">
          {{ hasExistingConversation ? t('TICKETS.EMAIL_CONVERSATION') : t('TICKETS.SEND_EMAIL') }}
        </h2>
        <button
          class="p-2 hover:bg-n-solid-4 rounded-lg transition-colors"
          @click="closeCompose"
        >
          <span class="i-lucide-x w-5 h-5 text-n-slate-11" />
        </button>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-hidden flex flex-col">
        <!-- If conversation exists and is email channel, show conversation view -->
        <div v-if="hasExistingConversation && conversation && isEmailConversation && conversation.id" class="flex-1 flex flex-col min-h-0">
          <div class="flex-1 flex flex-col min-h-0">
            <!-- Messages View -->
            <div class="flex-1 overflow-y-auto">
              <MessageList
                :messages="conversation.messages || []"
                :current-user-id="currentUser?.id"
                is-an-email-channel
                :inbox-supports-reply-to="{ incoming: true, outgoing: true }"
              />
            </div>
            <!-- Reply Box -->
            <div class="border-t border-n-weak">
              <ReplyBox />
            </div>
          </div>
        </div>

        <!-- Conversation is not email channel -->
        <div v-else-if="hasExistingConversation && !isLoadingConversation && conversation && !isEmailConversation" class="flex-1 flex items-center justify-center">
          <div class="text-center">
            <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-n-solid-4 mb-4">
              <span class="i-lucide-mail-x w-6 h-6 text-n-slate-10" />
            </div>
            <p class="text-sm text-n-slate-11">{{ t('TICKETS.CONVERSATION_NOT_EMAIL') }}</p>
          </div>
        </div>

        <!-- Loading conversation -->
        <div v-else-if="hasExistingConversation && isLoadingConversation" class="flex-1 flex items-center justify-center">
          <div class="text-center">
            <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-n-solid-4 mb-4">
              <svg
                class="h-6 w-6 text-n-slate-10 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                />
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
            <p class="text-sm text-n-slate-11">{{ t('TICKETS.LOADING_CONVERSATION') }}</p>
          </div>
        </div>

        <!-- If no conversation or conversation is not email, show compose form -->
        <div v-else-if="!hasExistingConversation || !isEmailConversation" class="flex-1 overflow-y-auto flex items-center justify-center p-8">
          <ComposeNewConversationForm
            class="w-full max-w-2xl"
            :contacts="contacts"
            :is-loading="isSearching || isSending"
            :current-user="currentUser"
            :selected-contact="selectedContact"
            :target-inbox="targetInbox"
            :is-creating-contact="isCreatingContact"
            :is-fetching-inboxes="isFetchingInboxes"
            :is-direct-uploads-enabled="directUploadsEnabled"
            :contact-conversations-ui-flags="uiFlags"
            :contacts-ui-flags="contactsUiFlags"
            :message-signature="messageSignature"
            :send-with-signature="sendWithSignature"
            @search-contacts="onContactSearch"
            @reset-contact-search="resetContacts"
            @update-selected-contact="handleSelectedContact"
            @update-target-inbox="handleTargetInbox"
            @clear-selected-contact="clearSelectedContact"
            @create-conversation="createConversation"
            @discard="closeCompose"
          />
        </div>
      </div>
    </div>
    </div>
  </Teleport>
</template>